'use client';

import React, {useCallback, useId, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import type {Placement, Props} from './typings';

import {StyledTooltipContent, StyledTooltipContentArrow} from './styles';
import {composeRefs, getRecalculatedPosition, mergeHandlers} from './utils';

/**
 * @description Tooltip component
 * - Wraps a single React element as the triggerElement/target
 * - Renders tooltip content into a React Portal
 * - Auto-positions and flips to stay within viewport bounds
 * - Supports hover and keyboard focus
 * - Accessible with ARIA attributes
 * @param {object} props
 * @param {React.ReactNode} props.content - The content to display in the tooltip
 * @param {"top" | "bottom" | "left" | "right" | "auto"} props.placement - The placement of the tooltip ( optional, defaults to `'top'`)
 * @param {number} props.offset - The offset distance of the tooltip from the target (optional, defaults to `8`)
 * @param {object} props.restHtmlProps - Additional HTML Element basic attributes to spread onto the tooltip content element
 * @param {object} ref - Ref forwarded to the trigger( children ) element;
 * Forwarding the ref allows parent components to directly access the DOM node
 * of the trigger element  (e.g., the button inside Tooltip)
 * This is useful for cases where the parent wants to:
 * - Focus the trigger programmatically, eg parent controlling focus or scroll.
 * - Measure its position or size.
 * - Child component needing its own ref for logic
 * - Integrate with other libraries that require a DOM node reference.
 * @returns {JSX.Element} The trigger element wrapped with tooltip
 */
const Tooltip = React.forwardRef<HTMLElement, Props>(
  ({content, placement = 'top', offset = 8, disabled, children, ...restHtmlProps}, ref) => {
    const id = useId();
    const [coordinates, setCoordinates] = useState<{x: number; y: number} | null>(null);
    const [activePlacement, setActivePlacement] = useState<Exclude<Placement, 'auto'>>(
      placement === 'auto' ? 'top' : placement,
    );
    const [isPositioned, setIsPositioned] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const triggerRef = useRef<HTMLElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);

    /**
     * @description Tooltip Reposition logic
     */
    const recalculatePosition = useCallback(() => {
      const triggerElement = triggerRef.current;
      const tooltipElement = tooltipRef.current;
      if (!triggerElement || !tooltipElement) return;

      const {chosenCoords, chosenPlacement} = getRecalculatedPosition({
        triggerElement,
        tooltipElement,
        placement,
        offset,
      });

      setActivePlacement(chosenPlacement);
      setCoordinates({x: chosenCoords!.clampedX, y: chosenCoords!.clampedY});
      setIsPositioned(true);
    }, [offset, placement]);

    /**
     * @description Show the tooltip
     * on hover/focus interactions
     */
    const handleShow = useCallback(() => {
      if (disabled) return;
      setIsOpen(true);
    }, [disabled]);

    /**
     * @description Hide the tooltip
     * on hover/focus interactions
     */
    const handleDismiss = useCallback(() => {
      setIsOpen(false);
    }, []);

    /**
     * @description Clone child in order to:
     * - Ensure that the Tooltip component receives exactly one child element (the trigger).
     * - Show an error if more than one child is passed, enforcing correct usage.
     * - Allow you to safely clone and augment the trigger element with refs and event handlers.
     */
    const child = React.Children.only(children);
    /**
     * @description Generate `aria-describedby` value for Tooltip
     * - A unique aria-describedby value when the tooltip is open.
     * - That value is used to link the trigger element to the tooltip for accessibility (screen readers).
     * When the tooltip is open, the trigger’s `aria-describedby` points to the tooltip’s ID,
     * so assistive technology knows what content describes the trigger.
     * @link https://www.w3.org/TR/wai-aria-1.1/#tooltip
     */
    const describedBy = isOpen ? `tooltip-${id}` : undefined;
    const childProps = {
      onMouseEnter: mergeHandlers(child.props.onMouseEnter, handleShow),
      onMouseLeave: mergeHandlers(child.props.onMouseLeave, handleDismiss),
      onFocus: mergeHandlers(child.props.onFocus, handleShow),
      onBlur: mergeHandlers(child.props.onBlur, handleDismiss),
      'aria-describedby':
        [child.props['aria-describedby'], describedBy].filter(Boolean).join(' ') || undefined,
    };

    /**
     * @description Recalculate on isOpen, resize, scroll
     * - `useLayoutEffect` is used here instead of `useEffect` because
     * it runs synchronously after all DOM mutations but before the browser paints.
     * - It ensures the tooltip is measured and positioned before the browser
     * renders the next frame, preventing visual flicker or "jumping" as the tooltip appears.
     * - Advantage with `useLayoutEffect`, the tooltip is always correctly positioned as soon as it appears,
     * resulting in a smoother, flicker-free user experience.
     */
    useLayoutEffect(() => {
      if (!isOpen) return;
      setIsPositioned(false);
      recalculatePosition();

      /**
       * @description Resize observer for tooltip repositioning,
       * observes changes to the trigger and tooltip elements
       */
      const resizeObserver = new ResizeObserver(() => recalculatePosition());
      if (triggerRef.current) resizeObserver.observe(triggerRef.current as Element);
      if (tooltipRef.current) resizeObserver.observe(tooltipRef.current as Element);

      /**
       * @description Scroll and resize event listeners
       */
      const onScroll = () => recalculatePosition();
      const onResize = () => recalculatePosition();
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', onResize);

      return () => {
        /**
         * @description Cleanup observers and event listeners
         */
        resizeObserver.disconnect();
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', onResize);
      };
    }, [isOpen, recalculatePosition]);

    /**
     * Calculate arrow position so it always points to the center of the trigger
     */
    let arrowLeft: number | string = '50%';
    if (coordinates && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = tooltipRef.current.offsetWidth;
      const rawArrowLeft = triggerRect.left + triggerRect.width / 2 - coordinates.x;
      const margin = 8;
      arrowLeft = Math.max(margin, Math.min(rawArrowLeft, tooltipWidth - margin));
    }

    return (
      <>
        {/*
         * creates a new React.Element based on the original children,
         * but with additional or overridden props (`childProps`).
         * ensures the Tooltip logic (show/hide, positioning, accessibility) is attached
         * to the trigger, even if the original child did not have these props.
         */}
        {React.cloneElement(child, {
          ...childProps,
          /**
           * Forward Tooltip's ref and child's ref, and set triggerRef.current
           */
          ref: composeRefs(ref, child.props.ref, (node: HTMLElement | null) => {
            triggerRef.current = node;
          }),
        })}

        {isOpen &&
          typeof document !== 'undefined' &&
          createPortal(
            <StyledTooltipContent
              ref={tooltipRef}
              id={`tooltip-${id}`}
              role="tooltip"
              data-placement={activePlacement}
              style={{
                left: coordinates ? coordinates.x : -9999,
                top: coordinates ? coordinates.y : -9999,
                opacity: isPositioned && coordinates ? 1 : 0,
                transform:
                  isPositioned && coordinates
                    ? 'translateY(0)'
                    : activePlacement === 'top'
                      ? 'translateY(10px)'
                      : activePlacement === 'bottom'
                        ? 'translateY(-10px)'
                        : activePlacement === 'left'
                          ? 'translateX(10px)'
                          : 'translateX(-10px)',
              }}
              $placement={activePlacement}
              $isVisible={isPositioned && !!coordinates}
              {...restHtmlProps}
            >
              <StyledTooltipContentArrow
                aria-hidden
                $placement={activePlacement}
                style={{
                  left:
                    activePlacement === 'top' || activePlacement === 'bottom'
                      ? arrowLeft
                      : undefined,
                }}
              />

              <div>{content}</div>
            </StyledTooltipContent>,
            document.body,
          )}
      </>
    );
  },
);

Tooltip.displayName = 'Tooltip';

export default Tooltip;
